/**
 * AI 成效頁（`#/ai-observability`／`#/system/ai`）。
 *
 * 版面四段，順序就是「先看結論、再看原因」：
 *   1. MetricStrip    回覆數／總成本／平均延遲／轉人工率（`@tpte/ui` StatCard，帶 delta）
 *   2. 趨勢            `@tpte/ui` 的 `LineChart`（回覆數與轉人工兩條線）
 *   3. RankBarList    需人工原因 Top N（`v_needs_human_reasons`）
 *   4. VersionCompare 人設版本 A/B（`v_persona_version_performance`）
 *
 * **資料來源：`GET /api/admin/ai/observability?range=24h|7d|30d`**（worker
 * `ai-observability.ts`）。這一頁以前整頁是本地 mock（`loadAiObservability()` 回假數字），
 * 2026-10 起改成打真端點：數字全部來自 D1 的 `auto_reply_calls` 與三個 view，
 * 前端不再自己造任何一組數列、也不再自己算回覆數。
 *
 * ## 狀態（五種，互不同形——這是這一頁最重要的事）
 *
 * | 狀態 | 觸發 | 畫面 |
 * |:--|:--|:--|
 * | loading      | 請求中 | 骨架，不留上一個範圍的舊數字 |
 * | empty        | 200 且真的 0 筆 | 「近 N 天還沒有 AI 回覆紀錄」（**不是錯誤**） |
 * | unauthorized | 401／403／沒帶 Admin Token | 「要 Admin Token」＋上方設定，不是重新登入 |
 * | invalid      | 400／422 | 「查詢條件有誤」＋換範圍 |
 * | offline      | 5xx／斷網／timeout | 「讀取失敗，稍後再試」＋重試 |
 * | error        | 格式不對／後端設定缺 | 「讀取失敗，回應無法顯示」＋重試 |
 *
 * 規則：**只有 401／403 可以說「權限」**。以前所有非 2xx 都往上丟同一種 `Error`，於是
 * Worker 500 或斷網也長成「需要管理權限」——使用者會以為是自己帳號不能看，就不重試了。
 * 判定集中在 `ai-observability-failure.ts`，四態的「空 vs 錯」交給 `resolveDataState()`
 * （`@chat-rea/console-ui` 的既有工具），這一頁不再自己寫一套 `rows.length === 0`。
 *
 * `?mock=loading|empty|unauthorized|invalid|offline|error` 可強制各態（只有 dev 用；
 * 正式站不帶參數）。
 *
 * 元件來源：MetricStrip／RankBarList／VersionCompare／RangeTabs／ObsSection 直接匯入
 * `@tpte/observability-ui`（`apps/console/package.json` 的 registry 版號 `0.1.2`，`src/index.css`
 * 對應 `@source`，由 `scripts/check-sources.mjs` 守門）。以前是整個目錄的手抄拷貝，
 * 手抄版已刪；只剩 `components/obs/format.ts`（金額格式與套件 0.1.2 有差異，見該檔與
 * `components/obs/README.md`）。
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, EmptyState, LineChart, LoadingState } from '@tpte/ui';
import { resolveDataState, type DataState, type DataErrorKind } from '@chat-rea/console-ui';

import {
  buildRangeSpec,
  MetricStrip,
  ObsSection,
  RangeTabs,
  RankBarList,
  VersionCompare,
  type CompareMetric,
  type MetricCard,
  type RangeKey,
  type RankItem
} from '@tpte/observability-ui';
/* 唯一的本地差異：舊版 `formatMicroCost` 會印 `US$0.0003`，套件 0.1.2 走 Intl 印 `$0.0003`。
 * 換掉會改動成本欄位的顯示字串，所以這一支留在本地（其餘面板元件已全部走套件）。 */
import { formatMicroCost } from '../components/obs/format';
import { useRelogin } from '../auth/AuthGate';
import { fetchAiObservability, type AiObsRange, type AiObsResponse } from './ai-observability-api';
import { AiObsError, type AiObsFailure, type AiObsFailureKind } from './ai-observability-failure';

type LoadState =
  | { status: 'loading' }
  | { status: 'failed'; failure: AiObsFailure }
  | { status: 'ready'; data: AiObsResponse };

/** 視覺驗收用的強制狀態（dev only）。 */
type ForcedState = 'loading' | 'empty' | 'ready' | 'network' | AiObsFailureKind;

const FORCED_STATES: readonly ForcedState[] = [
  'loading',
  'empty',
  'ready',
  'unauthorized',
  'invalid',
  'offline',
  'network',
  'error',
];

function forcedState(): ForcedState | null {
  if (typeof window === 'undefined') return null;
  const value = new URLSearchParams(window.location.search).get('mock');
  return FORCED_STATES.find((item) => item === value) ?? null;
}

/**
 * 每一種失敗的文案。**五種狀態的標題必須彼此不同**——使用者的下一步完全不同：
 * 重新登入／換範圍／等一下就重試／回報格式問題。
 */
const FAILURE_COPY: Record<
  AiObsFailureKind,
  { title: string; hint: string; retry: boolean; relogin: boolean; tone: string }
> = {
  unauthorized: {
    title: '要 Admin Token，不是登入角色',
    hint: 'AI 成效打的是管理端點，要 X-Admin-Token。engineer 或 admin 角色看得到監控與運行參數，但過不了這一頁。請用上方「設定 Admin Token」。重新登入不會補上這把鑰匙。',
    retry: false,
    relogin: false,
    tone: 'var(--ak-color-warning, #d97706)',
  },
  invalid: {
    title: '查詢條件有誤',
    hint: '後端不接受這次的時間範圍或參數。換一個範圍再試即可；這不是權限問題，也不代表沒有資料。',
    retry: true,
    relogin: false,
    tone: 'var(--ak-color-warning, #d97706)',
  },
  offline: {
    title: '讀取失敗，稍後再試',
    hint: '後端沒有回應或網路斷了，數字沒有載入成功——不代表近期沒有回覆紀錄。可以按重試。',
    retry: true,
    relogin: false,
    tone: 'var(--ak-color-danger, #dc2626)',
  },
  error: {
    title: '讀取失敗，回應無法顯示',
    hint: '後端有回應但內容看不懂（或這個 console 還沒接上 API）。這不是「沒有資料」，可以按重試。',
    retry: true,
    relogin: false,
    tone: 'var(--ak-color-danger, #dc2626)',
  },
};

/** `?mock=<kind>` 用的假失敗（只給視覺驗收；走的是同一條 `AiObsError` 分類路徑）。 */
function failureForced(kind: ForcedState): AiObsFailure {
  switch (kind) {
    case 'unauthorized':
      return { kind, status: 401, message: '後端拒絕 X-Admin-Token（unauthenticated）。登入角色補不上這把鑰匙。' };
    case 'invalid':
      return { kind, status: 400, message: '查詢條件後端不接受（invalid_range）：換一個時間範圍再試。' };
    case 'offline':
      return { kind, status: 500, message: '後端暫時出錯（internal_error），資料沒有載入成功；等一下再試。' };
    case 'network':
      return { kind: 'offline', status: null, message: '連不到後端（GET /api/admin/ai/observability）：Failed to fetch' };
    case 'error':
    default:
      return { kind: 'error', status: null, message: '後端回應格式不正確（缺少 stats／trend），無法顯示；這不是「沒有資料」。' };
  }
}


/**
 * `?mock=ready` 的樣本（**只是版面驗收用的假數字**，note 會寫出來，不會被當成真資料）。
 * 真站要看真數字請不要帶參數：那條路走 `fetchAiObservability()`。
 */
function sampleFixture(range: AiObsRange): AiObsResponse {
  const labels = range === '24h'
    ? ['00', '04', '08', '12', '16', '20']
    : ['一', '二', '三', '四', '五', '六', '日'];
  return {
    range,
    bucket: range === '24h' ? 'hour' : 'day',
    from: new Date(Date.now() - 7 * 864e5).toISOString(),
    to: new Date().toISOString(),
    empty: false,
    stats: {
      replies: 128,
      costUsd: 0.0412,
      avgLatencyMs: 2380,
      handoffs: 17,
      handoffRate: 0.117,
      deltas: { replies: 12.4, costUsd: -3.1, avgLatencyMs: 5.2, handoffRatePt: -1.8 },
    },
    trend: {
      labels,
      replies: [12, 19, 24, 31, 18, 24],
      handoffs: [2, 3, 3, 4, 2, 3],
    },
    reasons: [
      { id: 'price', label: '問價格／付款', value: 7, share: 0.41 },
      { id: 'refund', label: '要求退款', value: 5, share: 0.29 },
      { id: 'angry', label: '情緒激動', value: 3, share: 0.18 },
      { id: 'offtopic', label: '超出角色設定', value: 2, share: 0.12 },
    ],
    versions: {
      a: { id: 'p1', label: 'v1 人設', meta: '2026-09' },
      b: { id: 'p2', label: 'v2 人設', meta: '2026-10' },
      metrics: [
        { id: 'replies', label: '回覆數', a: 112, b: 128 },
        { id: 'handoff', label: '轉人工率', a: 0.152, b: 0.117, unit: '%', invert: true },
        { id: 'cost', label: '每則成本', a: 0.00038, b: 0.00032, unit: 'USD', invert: true },
      ],
      summary: 'v2 的回覆量上升、轉人工率下降，成本也略低。',
    },
    notes: ['（視覺驗收樣本資料：這是 ?mock=ready 的假數字，不是正式統計）'],
    generatedAt: new Date().toISOString(),
  };
}

/** 空資料的假回應（只給 `?mock=empty` 驗收用，數字全部是 0）。 */
function emptyFixture(range: AiObsRange): AiObsResponse {
  return {
    range,
    bucket: range === '24h' ? 'hour' : 'day',
    from: new Date().toISOString(),
    to: new Date().toISOString(),
    empty: true,
    stats: {
      replies: 0,
      costUsd: 0,
      avgLatencyMs: null,
      handoffs: 0,
      handoffRate: null,
      deltas: { replies: null, costUsd: null, avgLatencyMs: null, handoffRatePt: null },
    },
    trend: { labels: [], replies: [], handoffs: [] },
    reasons: [],
    versions: { a: { id: 'a', label: '—' }, b: { id: 'b', label: '—' }, metrics: [], summary: '' },
    notes: [],
    generatedAt: new Date().toISOString(),
  };
}

/**
 * 端點的 `stats` → 四張 KPI 卡。
 *
 * 為什麼在這一層才組卡片：`value` 的顯示格式是 **UI 的事**（成本要走 `formatMicroCost`，
 * 否則亞分金額會被四捨五入成 0）。端點只回原始數字，卡片在這裡成形。
 * `delta` 缺值一律 `null`（畫面顯示破折號），不要寫成 0%——那是在說「沒變」。
 */
function toMetricCards(stats: AiObsResponse['stats']): MetricCard[] {
  const replies = stats.replies;
  const handoffPct = stats.handoffRate === null ? '—' : (stats.handoffRate * 100).toFixed(1);
  return [
    {
      id: 'replies',
      label: '自動回覆',
      value: replies,
      unit: '則',
      delta: stats.deltas.replies,
      deltaSuffix: '%',
      hint: '本期成功送出的回覆則數',
    },
    {
      id: 'cost',
      label: '模型成本',
      value: formatMicroCost(stats.costUsd),
      delta: stats.deltas.costUsd,
      deltaSuffix: '%',
      invertDelta: true,
      hint: '模型＋工具呼叫，估算值（美元）',
    },
    {
      id: 'latency',
      label: '平均延遲',
      value: stats.avgLatencyMs === null ? '—' : stats.avgLatencyMs,
      unit: stats.avgLatencyMs === null ? undefined : 'ms',
      delta: stats.deltas.avgLatencyMs,
      deltaSuffix: '%',
      invertDelta: true,
      hint: '只平均成功回覆的 latency_ms',
    },
    {
      id: 'handoff',
      label: '轉人工率',
      value: handoffPct,
      unit: '%',
      delta: stats.deltas.handoffRatePt,
      deltaSuffix: 'pt',
      invertDelta: true,
      hint: '需人工介入 ÷（回覆＋需人工）；不是真人回覆率',
    },
  ];
}

/** 版本對比的指標補上格式化（成本是亞分金額，`formatCount` 會顯示成 0）。 */
function toCompareMetrics(rows: AiObsResponse['versions']['metrics']): CompareMetric[] {
  return rows.map((row) =>
    row.unit === 'USD'
      ? { ...row, format: (value: number) => formatMicroCost(value) }
      : { ...row },
  );
}

/**
 * 失敗狀態的卡片。`data-state` 是給測試／QA 斷言用的：中文字會改，狀態不會。
 * `unauthorized` 不給重新登入（角色補不上 Admin Token）、只有 `invalid`／`offline`／`error` 給重試。
 */
function FailureCard({
  failure,
  onRetry,
  onRelogin,
}: {
  failure: AiObsFailure;
  onRetry: () => void;
  onRelogin: () => void;
}) {
  const copy = FAILURE_COPY[failure.kind];
  /**
   * 左上角那一行刻意讓「5xx」與「斷網」也分得出來：兩者的下一步一樣（等一下、按重試），
   * 但看畫面的人要能一眼說出「是伺服器回的 500」還是「我這邊連不上」。
   */
  const code =
    failure.kind === 'offline'
      ? failure.status === null
        ? 'NETWORK · 無法連線'
        : `${failure.status} · 伺服器錯誤`
      : failure.status === null
        ? 'ERROR'
        : `${failure.status} · ERROR`;
  return (
    <Card className="min-h-[240px]">
      <div className="flex flex-col items-start gap-2 p-1" role="alert" data-testid="ai-obs-state" data-state={failure.kind}>
        <p className="m-0 font-mono text-[11px] uppercase tracking-[0.16em]" style={{ color: copy.tone }}>
          {code}
        </p>
        <h2 className="m-0 font-display text-base leading-[1.25] text-text">{copy.title}</h2>
        <p className="m-0 max-w-[60ch] text-xs leading-[1.7] text-text-tertiary">{copy.hint}</p>
        <p className="m-0 max-w-[60ch] break-words font-mono text-[11px] leading-[1.6] text-text-tertiary">
          {failure.message}
        </p>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {copy.retry ? (
            <button type="button" className="cui-btn cui-btn--sm cui-btn--primary" onClick={onRetry} data-testid="ai-obs-retry">
              重試
            </button>
          ) : null}
          {copy.relogin ? (
            <button
              type="button"
              className="cui-btn cui-btn--sm cui-btn--primary"
              onClick={onRelogin}
              data-testid="ai-obs-relogin"
            >
              重新登入
            </button>
          ) : null}
        </div>
      </div>
    </Card>
  );
}

/** 四態（`resolveDataState`）→ 這一頁的一種失敗。`invalid` 不屬於四態，另外標。 */
function failureKindOf(state: DataState, failure: AiObsFailure | null): AiObsFailureKind {
  if (failure === null) return 'error';
  if (failure.kind === 'invalid') return 'invalid';
  return state === 'unauthorized' || state === 'offline' || state === 'error' ? state : failure.kind;
}

export function AiObservabilityPage() {
  const [range, setRange] = useState<RangeKey>('7d');
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const relogin = useRelogin();

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    setState({ status: 'loading' });

    const forced = forcedState();
    if (forced !== null && forced !== 'loading') {
      if (forced === 'empty') {
        setState({ status: 'ready', data: emptyFixture(range as AiObsRange) });
      } else if (forced === 'ready') {
        setState({ status: 'ready', data: sampleFixture(range as AiObsRange) });
      } else {
        // 走跟真請求同一條分類：`AiObsError` 帶著 kind，畫面不必再猜一次。
        setState({ status: 'failed', failure: new AiObsError(failureForced(forced)) });
      }
      return () => {
        alive = false;
      };
    }
    if (forced === 'loading') {
      // 不發請求也不 resolve：看得到骨架，且不會在背景留下失敗的請求。
      return () => {
        alive = false;
      };
    }

    fetchAiObservability(range as AiObsRange, controller.signal)
      .then((data) => {
        if (alive) setState({ status: 'ready', data });
      })
      .catch((error: unknown) => {
        if (!alive) return;
        if (error instanceof DOMException && error.name === 'AbortError') return;
        if (error instanceof AiObsError) {
          setState({ status: 'failed', failure: { kind: error.kind, status: error.status, message: error.message } });
          return;
        }
        setState({
          status: 'failed',
          failure: { kind: 'error', status: null, message: error instanceof Error ? error.message : String(error) },
        });
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [range, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const spec = buildRangeSpec(range);
  const rangeLabel = range === '24h' ? '近 24 小時' : range === '7d' ? '近 7 天' : '近 30 天';

  const data = state.status === 'ready' ? state.data : null;
  const failure = state.status === 'failed' ? state.failure : null;

  /**
   * 「空」與「錯」由 `resolveDataState()` 判定（不做第二套）：有錯誤一定是錯誤；
   * 只有真的 200＋0 筆才會是 empty。`invalid` 在四態裡算 `error`，畫面再用 kind 細分。
   */
  const dataState = useMemo<DataState>(() => {
    const errorKind: DataErrorKind | null =
      failure === null ? null : failure.kind === 'invalid' ? 'error' : failure.kind;
    return resolveDataState({
      loading: state.status === 'loading',
      error: failure?.message ?? null,
      errorKind,
      count: data !== null && !data.empty ? 1 : 0,
      loaded: state.status === 'ready',
    });
  }, [state.status, failure, data]);

  const failureKind = failureKindOf(dataState, failure);
  const metrics = useMemo(() => (data === null ? [] : toMetricCards(data.stats)), [data]);
  const reasons = useMemo<RankItem[]>(
    () => (data === null ? [] : data.reasons.map((row) => ({ ...row }))),
    [data],
  );
  const compareMetrics = useMemo(() => (data === null ? [] : toCompareMetrics(data.versions.metrics)), [data]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3 md:px-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-col">
          <h1 className="m-0 font-display text-base font-normal leading-[1.25] text-text">AI 成效</h1>
          <p className="m-0 text-xs leading-[1.7] text-text-tertiary">
            AI 自動回覆的量、成本、延遲與轉人工原因；人設版本成效以同角色同期比較。
          </p>
        </div>
        <RangeTabs value={range} onChange={setRange} idPrefix="ai-obs-range" />
      </div>

      {dataState === 'loading' ? (
        <Card className="min-h-[240px]">
          <LoadingState label="正在載入 AI 成效…" />
        </Card>
      ) : failure !== null ? (
        <FailureCard failure={{ ...failure, kind: failureKind }} onRetry={retry} onRelogin={relogin} />
      ) : dataState === 'empty' ? (
        <Card className="min-h-[240px]">
          <div data-testid="ai-obs-state" data-state="empty">
            <EmptyState
              title={`${rangeLabel}還沒有 AI 回覆紀錄`}
              description="這段期間一筆自動回覆呼叫都沒有；換一個時間範圍，或確認 AI 回覆是不是開著。"
            />
          </div>
        </Card>
      ) : data === null ? (
        <Card className="min-h-[240px]">
          <LoadingState label="正在載入 AI 成效…" />
        </Card>
      ) : (
        <>
          <MetricStrip metrics={metrics} columns={4} rangeLabel={rangeLabel} />

          <ObsSection
            title="回覆量與轉人工"
            description="實線＝自動回覆則數；轉人工為同時段被判定「需要人看」的輪數。"
            actions={<span className="font-mono text-xs text-text-tertiary">{rangeLabel}</span>}
          >
            {data.trend.labels.length === 0 ? (
              <EmptyState compact title="這個範圍沒有趨勢資料" icon={null} />
            ) : (
              <LineChart
                labels={data.trend.labels}
                series={[
                  { name: '自動回覆', data: data.trend.replies },
                  { name: '轉人工', data: data.trend.handoffs },
                ]}
                height={180}
                showDots={range !== '30d'}
                ariaLabel="AI 回覆與轉人工趨勢"
                emptyLabel="沒有趨勢資料"
              />
            )}
          </ObsSection>

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
            <ObsSection
              title="需人工原因"
              description="被標記的原因次數，佔比＝佔同時段需人工總數。"
              pad="none"
            >
              <RankBarList
                items={reasons}
                showRank
                showShare
                ariaLabel="需人工原因排行"
                emptyLabel="這段期間沒有人工接手"
              />
            </ObsSection>

            <ObsSection title="人設版本成效" description="A／B 為同一批角色的前後兩版，越低越好的指標已標註。">
              <VersionCompare
                versionA={data.versions.a}
                versionB={data.versions.b}
                metrics={compareMetrics}
                summary={data.versions.summary}
                emptyLabel="沒有可比較的人設版本"
              />
            </ObsSection>
          </div>

          <div className="flex flex-col gap-1 pb-2">
            <p className="m-0 font-mono text-[11px] leading-[1.6] text-text-tertiary">
              {spec.key} · {data.bucket === 'hour' ? '逐時' : '逐日'} · {data.trend.labels.length} 桶 · 來源
              GET /api/admin/ai/observability · 產生於 {new Date(data.generatedAt).toLocaleString('zh-TW')}
            </p>
            {data.notes.map((note) => (
              <p key={note} className="m-0 font-mono text-[11px] leading-[1.6] text-text-tertiary">
                · {note}
              </p>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
